import { gql } from "apollo-boost";
import React from "react";
import { FiPlayCircle } from "react-icons/fi/";
import styled from "styled-components/macro";
import Box from "../../../components/Box";
import Button from "../../../components/Button";
import FormattedText from "../../../components/FormattedText";
import { Col, Container, Row } from "../../../components/Grid";
import { Heading, SubHeading, Title } from "../../../components/Headings";
import Image from "../../../components/Image";
import Link from "../../../components/Link";
import { Section } from "../../../components/Sections";
import Text from "../../../components/Text";
import NumberFormat from "../NumberFormat";
import Product from "../Product";
import ProductCard from "../ProductCard";

export function HeadingSection({
  product,
  producer,
  uri,
  basePath,
  overlap,
  ...styles
}) {
  return (
    <Section {...styles}>
      <Product>
        <Product.Visual width={[1 / 4, 1 / 6]} minWidth={96}>
          <Product.Image
            image={product.bottleImage}
            wineType={product.wineType}
            wineColor={product.wineColor}
            {...(overlap ? { boxShadow: "sm", mt: "-50%" } : {})}
            p={2}
            bg="background"
          />
        </Product.Visual>

        <Product.Main flex="1" py={[3, 4]} pl={[2, 3]}>
          <Product.Stand stand={product.stand} />
          {producer && (
            <Product.Producer
              to={
                uri &&
                `${uri}/../../${basePath}producers/${producer.producerId}`
              }
              producerName={producer.producerName}
            />
          )}
          <Product.Name component={Title} product={product} />
          <Product.Varieties text={product.varietiesText} />
        </Product.Main>
      </Product>
    </Section>
  );
}

export function ProductActions({ product }) {
  if (!product) return null;

  return (
    <Row flexWrap="nowrap" alignItems="center">
      <Shop url={product.webshop} />
      <Voucher url={product.webshop} {...product.voucher} />
    </Row>
  );
}

function Shop({ url }) {
  if (!url) return null;
  return (
    <Col>
      <Button href={url} size="sm" variant="primary">
        Kaufen
      </Button>
    </Col>
  );
}

function Voucher({ code, description, url }) {
  if (!code || !url) return null;
  const parts = (description || "{code}").split("{code}");
  return (
    <Col>
      <Text variant="single">
        {parts.map((part, index) => (
          <React.Fragment key={part}>
            {part}
            {index !== parts.length - 1 && <Em>{code}</Em>}
          </React.Fragment>
        ))}
      </Text>
    </Col>
  );
}

function Em(props) {
  return <Text as="em" color="primary" fontStyle="normal" {...props} />;
}

export function ProductBody({ product }) {
  return (
    <>
      <DescriptionSection product={product} />
      <AwardsSection product={product} />
      <TastingNotesSection product={product} />
      <AnalyticsSection product={product} />
      <VineyardSection product={product} />
      <VinificationSection product={product} />
      <PairingSection product={product} />
      <PalateSection product={product} />
      <VideoSection product={product} />
    </>
  );
}

export function DescriptionSection({ product }) {
  if (!product || !product.wineDescription) return null;
  return (
    <Section>
      <SubHeading>Weinbeschreibung</SubHeading>
      <FormattedText>{product.wineDescription}</FormattedText>
    </Section>
  );
}

export function AwardsSection({ product }) {
  if (!product || !product.awards) return null;
  if (!product.awards.length) return null;
  return (
    <Section>
      <SubHeading>Auszeichnungen</SubHeading>
      {product.awards.map(({ award, medal, text }, index) => (
        <React.Fragment key={`${award}-${index}`}>
          {(award || medal) && (
            <Text {...(text ? { mb: 0 } : {})}>
              {medal ? <>{medal} – </> : undefined}
              {award}
            </Text>
          )}
          <FormattedText>{text}</FormattedText>
        </React.Fragment>
      ))}
    </Section>
  );
}

export function TastingNotesSection({ product }) {
  if (!product || !product.tastingNotes) return null;
  if (!product.tastingNotes.length) return null;
  return (
    <Section>
      <SubHeading>Bewertungen</SubHeading>
      {product.tastingNotes.map(
        ({ publication, rating, note, date, author, externalUrl }) => (
          <Box key={`${publication}-${date}`} mb={2}>
            {(publication || rating) && (
              <SubHeading as="h4" pb={1}>
                {[publication, rating].filter(Boolean).join(": ")}
              </SubHeading>
            )}
            {note && <FormattedText mb={0}>{note}</FormattedText>}
            {author /* || date */ && (
              <Text>{[author /* date */].filter(Boolean).join(", ")}</Text>
            )}
          </Box>
        )
      )}
    </Section>
  );
}

export function AnalyticsSection({ product }) {
  const { alcohol, designation, residualSugar, totalAcidity } = product || {};
  return (
    <Row>
      {alcohol && (
        <Col width={[(1, 1 / 2)]}>
          <Section>
            <SubHeading>Alkohol</SubHeading>
            <Text variant="large">
              {alcohol.value != null ? (
                <>
                  <NumberFormat>{alcohol.value}</NumberFormat>%
                </>
              ) : (
                alcohol.text
              )}
            </Text>
          </Section>
        </Col>
      )}

      <Col width={[(1, 1 / 2)]}>
        {(designation || residualSugar || totalAcidity) && (
          <Section>
            <SubHeading>Analysedaten</SubHeading>
            {designation && (
              <Text variant="large" mb={2}>
                {designation}
              </Text>
            )}
            {residualSugar && (
              <Text mb={0}>
                {residualSugar.value != null ? (
                  <>
                    <NumberFormat>{residualSugar.value}</NumberFormat> g/l
                    Restzucker
                  </>
                ) : (
                  residualSugar.text
                )}
              </Text>
            )}
            {totalAcidity && (
              <Text mb={0}>
                {totalAcidity.value != null ? (
                  <>
                    <NumberFormat>{totalAcidity.value}</NumberFormat> g/l Säure
                  </>
                ) : (
                  totalAcidity.text
                )}
              </Text>
            )}
          </Section>
        )}
      </Col>
    </Row>
  );
}

export function VineyardSection({ product }) {
  if (!product) return null;
  const { vineyard, vineyardInformation } = product;
  if (!vineyard && !vineyardInformation) return null;
  return (
    <Section>
      <SubHeading>Lage{vineyard && <>: {vineyard}</>}</SubHeading>
      {vineyardInformation && (
        <FormattedText>{vineyardInformation}</FormattedText>
      )}
    </Section>
  );
}

export function VinificationSection({ product }) {
  if (!product || !product.vinification) return null;
  return (
    <Section>
      <SubHeading>Vinifizierung</SubHeading>
      <FormattedText>{product.vinification}</FormattedText>
    </Section>
  );
}

export function PairingSection({ product }) {
  if (!product || !product.pairing) return null;
  return (
    <Section>
      <SubHeading>Speisebegleitung</SubHeading>
      <FormattedText>{product.pairing}</FormattedText>
    </Section>
  );
}

export function PalateSection({ product }) {
  if (!product || !product.palate) return null;
  return (
    <Section>
      <SubHeading>Trinkempfehlung</SubHeading>
      <FormattedText>{product.palate}</FormattedText>
    </Section>
  );
}

export function VideoSection({ product }) {
  if (!product || !product.video || !product.video.url) return null;
  return (
    <Section>
      <SubHeading>Videobeschreibung</SubHeading>
      <Box as="a" href={product.video.url} position="relative" display="block">
        <Image url={product.video.previewImage} />
        <PlayOverlay>
          <FiPlayCircle />
        </PlayOverlay>
      </Box>
    </Section>
  );
}

const PlayOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  font-size: 64px;
  margin-top: -32px;
  margin-left: -32px;
  color: #fff;
  opacity: 0.8;
  transition: opacity 0.3s ease-out;
  a > &:hover {
    opacity: 1;
  }
`;

export function OtherProductsSection({ products, producer, uri, basePath }) {
  if (!products || !products.length) return null;
  return (
    <Section bg="soft.0" py={5} my={5}>
      <Container>
        {producer && (
          <Row alignItems="center" mb={4}>
            <Col flex="1">
              <Heading>
                Mehr von{" "}
                {(producer.producerName && producer.producerName.shortName) ||
                  ""}
              </Heading>
            </Col>
            {uri && (
              <Col>
                <Link
                  variant="more"
                  to={`${uri}/../../${basePath}producers/${
                    producer.producerId
                  }`}
                >
                  Profil zeigen
                </Link>
              </Col>
            )}
          </Row>
        )}

        <Row>
          {products.map(
            product =>
              product && (
                <Col key={product.id} width={[1 / 3, 1 / 6]} minWidth={96}>
                  <ProductCard
                    bg="background"
                    variant="mini"
                    to={`../../products/${product.id}`}
                    {...product}
                    producer={undefined}
                  />
                </Col>
              )
          )}
        </Row>
      </Container>
    </Section>
  );
}

export const fragments = {
  body: gql`
    fragment ProductBody on Product {
      awards {
        award
        medal
        text
      }
      tastingNotes {
        publication
        rating
        note
        author
        date
        externalUrl
      }
      alcohol {
        value
        text
      }
      totalAcidity {
        value
        text
      }
      residualSugar {
        value
        text
      }
      designation
      wineDescription
      vineyard
      vineyardInformation
      vinification
      pairingDescription
      palate # The drink recommendation is currently put there.
      webshop
      video {
        url
        previewImage
      }
      voucher {
        code
        description
      }
    }
  `
};
